import { FC, useContext, useState } from 'react';
import { RoomTypeGrp } from 'ts/hotel/classes/FilterModel';
import { useTranslation } from 'react-i18next';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faPerson, faTimes } from '@fortawesome/free-solid-svg-icons';
import { formatPriceCurrency } from 'utils/utils';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface FilterRoomTypeProps {
  type?: RoomTypeGrp;
  onChange: (group: RoomTypeGrp) => void;
  top?: string[];
}

const FilterRoomType: FC<FilterRoomTypeProps> = ({ type, onChange, ...props }) => {
  const { t } = useTranslation();
  const { filter } = useContext(HotelFilterContext);
  const max = 20;
  const [maxLine, setMaxLine] = useState(max);
  const [isExpanded, setIsExpanded] = useState(false);

  const showMore = () => {
    if (maxLine === max) {
      setMaxLine(type!.options.length);
    } else {
      setMaxLine(max);
    }
  };

  const onClickCheckbox = (checked: boolean, index: number) => {
    const newType = { ...type! };
    newType.options[index].checked = checked;
    onChange(type!);
  };

  const onClickClear = () => {
    const newType = { ...type! };
    newType.options.forEach((opt) => (opt.checked = false));
    onChange(type!);
  };

  const toggleAccordion = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div>
      {!!type && (
        <div className="bb-1">
          <div
            className=" hover-grey cur-poi"
            style={{ padding: '1px 0', userSelect: 'none' }}
            onClick={toggleAccordion}
          >
            <div className="title text-lg mt-10 flex-between">
              <span className=" flex flex-v-center">
                <FontAwesomeIcon
                  icon={type.icon || faPerson}
                  size="xs"
                  className="mr-5 flex-center"
                />
                {t('hotel.' + type.title)}
                <span
                  className="text-light"
                  style={{
                    color: '#6c757d',
                    background: '#e9ecef',
                    padding: '2px 8px',
                    borderRadius: 10,
                    margin: '0 7px',
                  }}
                >
                  {type.options.length}
                </span>
              </span>
              <FontAwesomeIcon
                icon={faChevronDown}
                style={{
                  transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'all 0.3s ease-in-out',
                }}
              />
            </div>
          </div>

          {isExpanded && (
            <div className="position-relative">
              <div className="filter-room-type-clear">
                {type.options.some((opt) => opt.checked) && (
                  <span
                    className="text-normal text-light ml-10 color-danger cur-poi text-nowrap"
                    onClick={onClickClear}
                  >
                    <span className="mr-5">{t('clear')}</span>
                    <FontAwesomeIcon icon={faTimes} />
                  </span>
                )}
              </div>
              {type.options.slice(0, maxLine).map((option, optIndex) => (
                <div className="w100p" key={optIndex}>
                  <Tooltip
                    position="top"
                    content={formatPriceCurrency(option.price, filter?.price.currencySign || '')}
                    key={optIndex}
                    children={
                      <Checkbox
                        htmlLabel={
                          <div className="flex-between">
                            <span className="text-limited text-slg text-lh">
                              <span
                                className={
                                  props.top?.includes(option.name) ? 'text-bold text-contrast' : ''
                                }
                              >
                                {option.name}{' '}
                              </span>
                              <span className="text-light text-xsm">({option.count})</span>
                            </span>
                          </div>
                        }
                        className="text-sm"
                        checked={option.checked}
                        onChange={(checked) => onClickCheckbox(checked, optIndex)}
                      />
                    }
                  />
                </div>
              ))}
              {type.options.length > maxLine && (
                <div className="btn btn-lnk display-ib" onClick={() => showMore()}>
                  {t('show_all')}
                </div>
              )}
              {maxLine > max && (
                <div className="btn btn-lnk display-ib" onClick={() => showMore()}>
                  {t('show_less')}
                </div>
              )}

              <div className="mb-10"></div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(FilterRoomType, 'FilterRoomType');
